
<script setup>
import { useDisplay } from 'vuetify'
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEventsStore } from '@/stores/events'
import EventCard from '@/components/EventCard.vue'
import Loading from '@/components/Loading.vue'
import { userBetsAPI } from '@/services/api'
import { useUserStore } from '@/stores/user'
import PageHeader from '@/components/PageHeader.vue'

// Composables
const { t } = useI18n()
const userStore = useUserStore()

// Vuetify display composable
const { smAndUp } = useDisplay()

// Store
const eventsStore = useEventsStore()

// Reactive data
// Bindiamo singolarmente i campi dei filtri allo store tramite computed
const team = computed({
  get() {
    return eventsStore.filters.team
  },
  set(val) {
    eventsStore.setFilters({ team: val })
  }
})

const sportKey = computed({
  get() {
    return eventsStore.filters.sport_key
  },
  set(val) {
    eventsStore.setFilters({ sport_key: val })
  }
})

// currentPage è memorizzata nello store (eventsStore.pagination.current_page)
// Usiamo un computed con getter/setter per mantenere la pagina nel store
const currentPage = computed({
  get() {
    return eventsStore.pagination.current_page || 1
  },
  set(value) {
    // Aggiorna solo il valore nel store
    eventsStore.pagination.current_page = value
  }
})

// Computed
const sportOptions = computed(() => {
  return [
    { title: t('events.sports.soccer_epl'), value: 'soccer_epl' },
    { title: t('events.sports.soccer_spain_la_liga'), value: 'soccer_spain_la_liga' },
    { title: t('events.sports.soccer_italy_serie_a'), value: 'soccer_italy_serie_a' },
    // { title: t('events.sports.americanfootball_nfl'), value: 'americanfootball_nfl' },
    // { title: t('events.sports.americanfootball_nfl_super_bowl_winner'), value: 'americanfootball_nfl_super_bowl_winner' },
    // { title: t('events.sports.baseball_mlb_world_series_winner'), value: 'baseball_mlb_world_series_winner' },
    { title: t('events.sports.basketball_nba'), value: 'basketball_nba' },
    // { title: t('events.sports.basketball_nba_preseason'), value: 'basketball_nba_preseason' },
    // { title: t('events.sports.baseball_mlb'), value: 'baseball_mlb' },
    // { title: t('events.sports.basketball_euroleague'), value: 'basketball_euroleague' },
    // { title: t('events.sports.basketball_nba_championship_winner'), value: 'basketball_nba_championship_winner' },
    // { title: t('events.sports.icehockey_nhl'), value: 'icehockey_nhl' },
    // { title: t('events.sports.icehockey_nhl_championship_winner'), value: 'icehockey_nhl_championship_winner' },
    // { title: t('events.sports.tennis_atp_shanghai_masters'), value: 'tennis_atp_shanghai_masters' },
    // { title: t('events.sports.tennis_wta_china_open'), value: 'tennis_wta_china_open' },
    // { title: t('events.sports.tennis_wta_wuhan_open'), value: 'tennis_wta_wuhan_open' }

    { title: t('events.sports.soccer_uefa_champs_league'), value: 'soccer_uefa_champs_league' },
    { title: t('events.sports.soccer_uefa_europa_league'), value: 'soccer_uefa_europa_league' },
    { title: t('events.sports.soccer_fifa_world_cup_qualifiers_europe'), value: 'soccer_fifa_world_cup_qualifiers_europe' },
  ]
})

// Methods
let searchTimeout = null
let filterTimeout = null

const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    applyFilters()
  }, 500)
}

const debouncedFilter = () => {
  clearTimeout(filterTimeout)
  filterTimeout = setTimeout(() => {
    applyFilters()
  }, 100) // Debounce molto breve per i filtri
}

const applyFilters = () => {
  // Creiamo i filtri puliti direttamente
  const filters = {
    sport_key: null,
    team: null,
    date_from: null,
    date_to: null
  }
  
  // I singoli computed scrivono già nello store, qui resettiamo la pagina e ricarichiamo

  // Una sola chiamata per settare tutti i filtri
  // Resetta la pagina nello store e ricarica (i filtri sono già nel store via computed)
  eventsStore.pagination.current_page = 1
  loadEvents()
}

const onSportChange = () => {
  debouncedFilter()
}

const onPageChange = async (page) => {
  currentPage.value = page
  // currentPage è un computed che scrive nello store
  await loadEvents()

  const eventsListElement = document.getElementById('events-list-container')
  if (eventsListElement) {
    const y = eventsListElement.getBoundingClientRect().top + window.pageYOffset - 80 // offset
    window.scrollTo({ top: y, behavior: 'smooth' })
  }
}

const loadEvents = async () => {
  await eventsStore.fetchEvents({
    page: currentPage.value,
    per_page: 10
  })
}

const refreshEvents = () => {
  loadEvents()
}

const clearAllFilters = () => {
  // Resetta i filtri sia localmente che nello store
  eventsStore.clearFilters()
  eventsStore.pagination.current_page = 1
  loadEvents()
}

const getFiltersSportIcon = () => {
  const sportKey = eventsStore.filters.sport_key
  const defaultIcon = 'mdi-soccer'
  if (!sportKey) return defaultIcon

  const sportIconMap = {
    soccer_epl: 'mdi-soccer',
    soccer_spain_la_liga: 'mdi-soccer',
    soccer_italy_serie_a: 'mdi-soccer',
    basketball_nba: 'mdi-basketball',
  }

  return sportIconMap[sportKey] || defaultIcon
}

// Lifecycle
onMounted(async () => {
  loadEvents()

  // Se non sono ancora stati caricate, carica scommesse dell'utente.
  if (!userStore.getMyBets()) {
    const responseMyBets = await userBetsAPI.getMyBets()
    if (responseMyBets.success) {
      userStore.updateMyBets(responseMyBets.data)
    }
  }
})
</script>


<template>
  <div class="events-list">
    <!-- Header -->
     <div class="mb-5">
      <PageHeader :title="$t('events.list.title')" icon="mdi-calendar" />
    </div>

    <!-- Filters -->
    <v-card class="mb-6" elevation="2">
      <!-- <v-card-title>
        <v-icon class="mr-2">mdi-filter</v-icon>
        {{ t('events.list.filters') }}
      </v-card-title> -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
            v-model="team"
            :label="t('events.list.searchTeam')"
            :prepend-icon="smAndUp ? 'mdi-magnify' : undefined"
            :class="{ 'hide-prepend-icon': !smAndUp }"
            clearable
            @input="debouncedSearch"
            hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="sportKey"
              :items="sportOptions"
              :label="t('events.list.sport')"
              :prepend-icon="smAndUp ? getFiltersSportIcon() : undefined"
              :class="{ 'hide-prepend-icon': !smAndUp }"
              clearable
              @update:model-value="onSportChange"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-btn
              color="primary"
              @click="refreshEvents"
              :loading="eventsStore.isLoading"
              class="mr-2"
            >
              <v-icon class="mr-2">mdi-refresh</v-icon>
              {{ t('events.list.refresh') }}
            </v-btn>
            <v-btn
              color="secondary"
              @click="clearAllFilters"
            >
              <v-icon class="mr-2">mdi-filter-off</v-icon>
              {{ t('events.list.clear') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Loading State -->
    <Loading v-if="eventsStore.isLoading" :message="t('events.list.loading')" />

    <!-- Error State -->
    <v-alert
      v-if="eventsStore.hasError"
      type="error"
      class="mb-4"
      closable
      @click:close="eventsStore.clearError"
    >
      <strong>Errore:</strong> {{ eventsStore.error }}
    </v-alert>

    <!-- Empty State -->
    <v-card v-if="!eventsStore.isLoading && !eventsStore.hasEvents" class="text-center py-8">
      <v-card-text>
        <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-calendar-blank</v-icon>
        <h3>{{ t('events.list.noEvents') }}</h3>
        <p class="text-body-2">{{ t('events.list.noEventsSubtitle') }}</p>
      </v-card-text>
    </v-card>

    <!-- Events List -->
    <v-fade-transition>
      <div v-if="eventsStore.hasEvents" class="events-container" id="events-list-container">
        <div class="d-flex justify-space-between align-center mb-4">
          <h2 class="text-h5">
            {{ t('events.list.eventsFound') }}: {{ eventsStore.totalEvents }}
          </h2>
          <v-chip color="primary" size="small">
            {{ t('events.list.page') }} {{ eventsStore.pagination.current_page }} {{ t('common.of') }} {{ eventsStore.pagination.last_page }}
          </v-chip>
        </div>

        <!-- Event Cards -->
        <v-row>
          <v-col
            v-for="event in eventsStore.events"
            :key="event.id"
            cols="12"
            lg="6"
          >
            <EventCard :event="event" />
          </v-col>
        </v-row>

        <!-- Pagination -->
        <div class="text-center mt-6">
          <v-pagination
            v-model="currentPage"
            :length="eventsStore.pagination.last_page"
            @update:model-value="onPageChange"
          />
        </div>
      </div>
    </v-fade-transition>
  </div>
</template>


<style scoped>
.events-list {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}
.page-subtitle {
  font-size: 1.1rem;
  color: #666;
  margin: 0;
}

.events-container {
  margin-top: 24px;
}
</style>

/* Nasconde l'icona prepend su schermi piccoli */
.hide-prepend-icon .v-input__prepend {
  display: none !important;
}